import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button, Chip } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDate, formatMoney } from '@/lib/format';
import type { CustomerJourney } from '@/api/customer-journey';
import { saleFollowerNames, isLostStep } from './sale-process-helpers';
import { SERVICE_INTEREST_LABELS } from './service-interest-options';
import { EditDealInfoDrawer } from './edit-deal-info-drawer';

/**
 * Journey Information (deal info) panel — the embedded
 * `customer_journey_explaination` over an InfoGrid: influencer, budget,
 * expected close, provider, problem/expected narrative, sale followers, and
 * interested services (Chips). "Edit deal info" opens a slide-over form. Locked
 * (won/lost) journeys are read-only; lost ones also show the failure reason.
 */

/** Interpret budget which the backend stores as a numeric string or number. */
function budgetValue(raw: unknown): string {
  if (raw == null || raw === '') return '—';
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return Number.isFinite(n) && n > 0 ? formatMoney(n) : '—';
}

function serviceLabel(value: string): string {
  return SERVICE_INTEREST_LABELS[value] ?? value;
}

export function JourneyDealInfoSection({
  customerId,
  journey,
}: {
  customerId: number;
  journey: CustomerJourney;
}) {
  const [editing, setEditing] = useState(false);
  const ex = journey.customer_journey_explaination;
  const locked = !!journey.customer_journey_locked;
  const followers = saleFollowerNames(journey);
  const services = ex?.service_interestings ?? [];

  const items: InfoItem[] = [
    { label: 'Decision maker', value: ex?.influencer_explaination || undefined },
    { label: 'Budget', value: budgetValue(ex?.budget_explaination) },
    { label: 'Expected close', value: formatDate(ex?.expected_date) },
    { label: 'Provider', value: ex?.provider_explaination || undefined },
    { label: 'Sale followers', value: followers.length ? followers.join(', ') : undefined },
    { label: 'Result', value: ex?.explaination_result || undefined },
    { label: 'Problem', value: ex?.problem_explaination || undefined, full: true },
    { label: 'Expectation', value: ex?.expected_explaination || undefined, full: true },
  ];

  return (
    <Card flat>
      <CardHeader
        actions={
          !locked ? (
            <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
              <Pencil size={14} style={{ marginRight: 'var(--vn-space-1)' }} />
              Edit deal info
            </Button>
          ) : undefined
        }
      >
        <CardTitle>Deal information</CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {isLostStep(journey.customer_journey_step) && ex?.failed_reason ? (
          <Alert tone="danger" title="Lost reason">
            {ex.failed_reason}
            {ex.failed_explanation ? ` — ${ex.failed_explanation}` : ''}
          </Alert>
        ) : null}

        <InfoGrid items={items} />

        {services.length ? (
          <div>
            <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: '0 0 var(--vn-space-1)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
              Interested services
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
              {services.map((s) => (
                <Chip key={s} tone="info" appearance="subtle" size="sm">
                  {serviceLabel(s)}
                </Chip>
              ))}
            </div>
          </div>
        ) : null}
      </CardBody>

      {!locked ? (
        <EditDealInfoDrawer
          customerId={customerId}
          journey={journey}
          open={editing}
          onOpenChange={setEditing}
        />
      ) : null}
    </Card>
  );
}

export default JourneyDealInfoSection;
